import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';

/**
 * UI-CONSISTENCY.1 — the ONE empty-state idiom.
 *
 * Centered faded icon + body2 message (+ optional caption hint and action).
 * Works standalone or inside a table row's colSpan cell. Unifies the drifted
 * py 6/7/8 paddings, icon opacities 0.2/0.25/0.3, and the icon-less report
 * empties.
 */

interface EmptyStateProps {
  /** MUI icon component (not an element), e.g. `icon={InventoryIcon}`. */
  icon: SvgIconComponent;
  message: React.ReactNode;
  /** Optional caption line under the message. */
  hint?: React.ReactNode;
  /** Optional action (e.g. a "create first" button). */
  action?: React.ReactNode;
}

const EmptyState: React.FC<EmptyStateProps> = ({ icon: Icon, message, hint, action }) => {
  const theme = useTheme();
  return (
    <Box sx={{ py: 6, textAlign: 'center', color: theme.palette.text.secondary }}>
      <Icon sx={{ fontSize: 40, mb: 1, opacity: 0.25 }} />
      <Typography variant="body2">{message}</Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 420, mx: 'auto', mt: 0.5 }}>
          {hint}
        </Typography>
      )}
      {action && <Box sx={{ mt: 1.5 }}>{action}</Box>}
    </Box>
  );
};

export default EmptyState;
